import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { InventoryItem, InventoryFilters } from '../../types/models';

interface InventoryState {
  items: InventoryItem[];
  filteredItems: InventoryItem[];
  filters: InventoryFilters;
  selectedItem: InventoryItem | null;
  selectedItemIds: string[];
  loading: boolean;
  error: string | null;
  groups: string[];
}

const initialState: InventoryState = {
  items: [],
  filteredItems: [],
  filters: {
    searchTerm: '',
    checkedOutOnly: false,
    checkedInOnly: false,
  },
  selectedItem: null,
  selectedItemIds: [],
  loading: false,
  error: null,
  groups: [],
};

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setItems: (state, action: PayloadAction<InventoryItem[]>) => {
      state.items = action.payload;
      state.groups = [...new Set(action.payload.map(item => item.itemGroup).filter(Boolean))] as string[];
      state.filteredItems = applyFilters(action.payload, state.filters);
    },
    addItem: (state, action: PayloadAction<InventoryItem>) => {
      state.items.push(action.payload);
      state.groups = [...new Set(state.items.map(item => item.itemGroup).filter(Boolean))] as string[];
      state.filteredItems = applyFilters(state.items, state.filters);
    },
    updateItem: (state, action: PayloadAction<InventoryItem>) => {
      const index = state.items.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
        state.filteredItems = applyFilters(state.items, state.filters);
      }
    },
    removeItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
      state.groups = [...new Set(state.items.map(item => item.itemGroup).filter(Boolean))] as string[];
      state.filteredItems = applyFilters(state.items, state.filters);
    },
    setFilters: (state, action: PayloadAction<Partial<InventoryFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.filteredItems = applyFilters(state.items, state.filters);
    },
    setSelectedItem: (state, action: PayloadAction<InventoryItem | null>) => {
      state.selectedItem = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {
        searchTerm: '',
        checkedOutOnly: false,
        checkedInOnly: false,
      };
      state.filteredItems = state.items;
    },
    clearError: (state) => {
      state.error = null;
    },
    toggleItemSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const index = state.selectedItemIds.indexOf(id);
      if (index === -1) {
        state.selectedItemIds.push(id);
      } else {
        state.selectedItemIds.splice(index, 1);
      }
    },
    selectAllItems: (state) => {
      state.selectedItemIds = state.filteredItems
        .map(item => item.id)
        .filter(Boolean) as string[];
    },
    clearSelection: (state) => {
      state.selectedItemIds = [];
    },
  },
});

// Helper function to apply filters
function applyFilters(items: InventoryItem[], filters: InventoryFilters): InventoryItem[] {
  let filtered = [...items];

  // Search term filter
  if (filters.searchTerm) {
    const searchLower = filters.searchTerm.toLowerCase();
    filtered = filtered.filter(
      item =>
        item.itemName.toLowerCase().includes(searchLower) ||
        (item.itemGroup && item.itemGroup.toLowerCase().includes(searchLower))
    );
  }

  // Group filter
  if (filters.group) {
    filtered = filtered.filter(item => item.itemGroup === filters.group);
  }

  // Status filters
  if (filters.checkedOutOnly) {
    filtered = filtered.filter(item => item.checkedOut);
  } else if (filters.checkedInOnly) {
    filtered = filtered.filter(item => !item.checkedOut);
  }

  return filtered;
}

export const {
  setLoading,
  setError,
  setItems,
  addItem,
  updateItem,
  removeItem,
  setFilters,
  setSelectedItem,
  clearFilters,
  clearError,
  toggleItemSelection,
  selectAllItems,
  clearSelection,
} = inventorySlice.actions;

export default inventorySlice.reducer;